/**
 * Plan List — displays the user's expedition plans.
 *
 * Rendered inside the Logbook's "Plans" tab. Uses planStorage
 * for CRUD operations on the new graph-model Plan entity.
 */

import { logger } from '../../utils/logger.js'
import { LitElement, html, css, nothing } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { planStorage } from '../../storage/plan-storage.js'
import { countsBySubject } from '../../storage/relationship-counts.js'
import { router } from '../../utils/router.js'
import type { Plan } from '../../schemas/plan.js'

import '@material/web/button/filled-button.js'
import '@material/web/button/outlined-button.js'
import '@material/web/iconbutton/icon-button.js'
import '@material/web/progress/linear-progress.js'
import '../common/confirm-dialog'
import type { ConfirmDialog } from '../common/confirm-dialog.js'

import { authService } from '../../services/auth/auth-service.js'
import type { AuthState } from '../../services/auth/auth-context.js'
import { syncListOnNavigate } from '../../storage/sync-manager.js'

const log = logger.create('plan-list')

@customElement('plan-list')
export class PlanList extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: hidden;
    }

    .sync-bar {
      --md-linear-progress-active-indicator-color: var(--cairn-primary-color, #3b82f6);
      --md-linear-progress-track-color: transparent;
      --md-linear-progress-track-height: 2px;
      --md-linear-progress-active-indicator-height: 2px;
      width: 100%;
      flex-shrink: 0;
    }

    .list-content {
      flex: 1;
      padding: var(--cairn-spacing-md, 16px);
      overflow-y: auto;
      min-height: 0;
      transition: transform 0.15s ease-out;
    }

    .add-button {
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    .add-button md-filled-button {
      width: 100%;
    }

    @media (min-width: 769px) {
      .add-button md-filled-button {
        width: auto;
      }
    }

    .controls {
      display: flex;
      gap: var(--cairn-spacing-sm, 8px);
      align-items: center;
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    .search-input {
      flex: 1;
      padding: 10px 12px;
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      font-size: var(--cairn-text-size-sm, 14px);
      background: var(--cairn-surface-color, #ffffff);
      color: var(--cairn-text-color, #1e293b);
      outline: none;
      transition: border-color var(--cairn-animation-duration, 0.2s);
    }

    .search-input:focus {
      border-color: var(--cairn-primary-color, #3b82f6);
    }

    .search-input::placeholder {
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .plan-grid {
      display: grid;
      gap: 16px;
      grid-template-columns: 1fr;
    }

    @media (min-width: 901px) and (max-width: 1300px) {
      .plan-grid {
        grid-template-columns: 1fr 1fr;
      }
    }

    @media (min-width: 1301px) and (max-width: 1800px) {
      .plan-grid {
        grid-template-columns: 1fr 1fr 1fr;
      }
    }

    @media (min-width: 1801px) {
      .plan-grid {
        grid-template-columns: 1fr 1fr 1fr 1fr;
      }
    }

    .plan-card {
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      padding: var(--cairn-spacing-md, 16px);
      cursor: pointer;
      transition: all var(--cairn-animation-duration, 0.2s);
      display: flex;
      flex-direction: column;
      gap: var(--cairn-spacing-sm, 8px);
      box-sizing: border-box;
      overflow: hidden;
    }

    .plan-card:hover {
      border-color: var(--cairn-primary-color, #3b82f6);
      box-shadow: var(--cairn-shadow, 0 2px 4px rgba(0, 0, 0, 0.1));
    }

    .card-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: var(--cairn-spacing-sm, 8px);
    }

    .card-title {
      flex: 1;
    }

    .plan-name {
      font-size: var(--cairn-text-size-lg, 20px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0;
    }

    .card-actions {
      display: flex;
      gap: var(--cairn-spacing-xs, 4px);
    }

    .meta {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .meta .material-icons {
      font-size: 16px;
    }

    .description {
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
      line-height: 1.4;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      text-overflow: ellipsis;
    }

    .related-counts {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .count-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      padding: 2px 8px;
      border-radius: 12px;
      background: var(--cairn-surface-hover-color, #f1f5f9);
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .count-chip .material-icons {
      font-size: 14px;
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: var(--cairn-spacing-xl, 32px);
      min-height: 300px;
    }

    .empty-icon {
      font-family: 'Material Symbols Outlined';
      font-size: 48px;
      margin-bottom: var(--cairn-spacing-md, 16px);
      color: var(--cairn-text-secondary-color, #64748b);
      opacity: 0.6;
    }

    .empty-title {
      font-size: var(--cairn-text-size-lg, 20px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0 0 var(--cairn-spacing-sm, 8px) 0;
    }

    .empty-description {
      font-size: var(--cairn-text-size-md, 16px);
      color: var(--cairn-text-secondary-color, #64748b);
      margin-bottom: var(--cairn-spacing-lg, 24px);
      max-width: 400px;
    }

    .material-icons {
      font-family: 'Material Symbols Outlined';
      font-weight: normal;
      font-style: normal;
      line-height: 1;
      letter-spacing: normal;
      text-transform: none;
      display: inline-block;
      white-space: nowrap;
      word-wrap: normal;
      direction: ltr;
      user-select: none;
    }
  `

  @state() private plans: Plan[] = []
  @state() private loaded = false
  @state() private syncing = false
  @state() private searchQuery = ''
  @state() private activityCountByPlan: Map<string, number> = new Map()

  private authState: AuthState = authService.getState()
  private unsubAuth?: () => void

  override connectedCallback(): void {
    super.connectedCallback()
    this.authState = authService.getState()
    this.unsubAuth = authService.onChange(state => {
      this.authState = state
      void this.loadPlans()
    })
    void this.loadPlans()
    this.syncing = true
    void syncListOnNavigate('plans')
      ?.then(() => this.loadPlans())
      .finally(() => {
        this.syncing = false
      })
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.unsubAuth?.()
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  }

  private handleVisibilityChange = (): void => {
    if (!document.hidden) {
      void this.loadPlans()
    }
  }

  private async loadPlans(): Promise<void> {
    try {
      const result = await planStorage.getPlansForUser(this.authState.userId)
      if (result.success && result.data) {
        this.plans = result.data
      }
      await this.loadCounts()
    } catch (error) {
      log.error('Failed to load plans:', error)
    } finally {
      this.loaded = true
    }
  }

  private async loadCounts(): Promise<void> {
    try {
      const activityCounts = await countsBySubject('has-activity', 'plan')
      this.activityCountByPlan = activityCounts
    } catch (error) {
      log.error('Failed to load related counts:', error)
    }
  }

  private renderCounts(activityCount: number): unknown {
    if (activityCount === 0) return ''
    return html`
      <div class="related-counts">
        <span class="count-chip">
          <span class="material-icons">directions_walk</span>
          ${activityCount} ${activityCount === 1 ? 'Activity' : 'Activities'}
        </span>
      </div>
    `
  }

  private get filteredPlans(): Plan[] {
    if (!this.searchQuery.trim()) return this.plans
    const q = this.searchQuery.toLowerCase().trim()
    return this.plans.filter(
      p =>
        (p.name.toLowerCase().includes(q) || p.location?.name.toLowerCase().includes(q)) ??
        p.description?.toLowerCase().includes(q)
    )
  }

  private handleSearchChange(e: Event): void {
    this.searchQuery = (e.target as HTMLInputElement).value
  }

  private handleAddPlan(): void {
    router.navigate('/plans/new')
  }

  private handleViewPlan(plan: Plan): void {
    router.navigate(`/plans/${plan.id}`)
  }

  private handleEditPlan(plan: Plan): void {
    router.navigate(`/plans/${plan.id}/edit`)
  }

  private async handleDeletePlan(plan: Plan): Promise<void> {
    const dialog = this.shadowRoot?.querySelector('confirm-dialog') as ConfirmDialog | undefined
    if (dialog) {
      dialog.headline = 'Delete Plan'
      dialog.message = `Delete "${plan.name}"? This cannot be undone.`
      const confirmed = await dialog.show()
      if (!confirmed) return
    }
    try {
      await planStorage.deletePlan(plan.id)
      await this.loadPlans()
    } catch (error) {
      log.error('Failed to delete plan:', error)
    }
  }

  private formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  private getDateRange(plan: Plan): string {
    if (plan.times.length === 0) return 'No date set'
    const sorted = [...plan.times].sort((a, b) => a.datetime.localeCompare(b.datetime))
    const first = this.formatDate(sorted[0].datetime)
    const last = this.formatDate(sorted[sorted.length - 1].datetime)
    return first === last ? first : `${first} - ${last}`
  }

  private pullStartY = 0
  private pulling = false
  private scrollerEl: HTMLElement | null = null

  private handleTouchStart(e: TouchEvent): void {
    const scroller = e.currentTarget as HTMLElement
    this.scrollerEl = scroller
    if (scroller.scrollTop <= 0) {
      this.pullStartY = e.touches[0].clientY
      this.pulling = true
    }
  }

  private handleTouchMove(e: TouchEvent): void {
    if (!this.pulling || !this.scrollerEl) return
    const dy = e.touches[0].clientY - this.pullStartY
    if (dy > 0) {
      const clamped = Math.min(dy * 0.4, 80)
      this.scrollerEl.style.transform = `translateY(${clamped}px)`
    } else {
      this.pulling = false
      this.scrollerEl.style.transform = ''
    }
  }

  private handleTouchEnd(e: TouchEvent): void {
    if (!this.pulling) return
    const dy = e.changedTouches[0].clientY - this.pullStartY
    this.pulling = false
    if (this.scrollerEl) this.scrollerEl.style.transform = ''
    if (dy > 60) {
      void this.refresh()
    }
  }

  private async refresh(): Promise<void> {
    this.syncing = true
    try {
      await syncListOnNavigate('plans')
      await this.loadPlans()
    } finally {
      this.syncing = false
    }
  }

  private renderPlanCard(plan: Plan): unknown {
    const dateRange = this.getDateRange(plan)
    const activityCount = this.activityCountByPlan.get(plan.id) ?? 0

    return html`
      <div class="plan-card" @click=${() => this.handleViewPlan(plan)}>
        <div class="card-header">
          <div class="card-title">
            <h3 class="plan-name">${plan.name}</h3>
          </div>
          <div class="card-actions" @click=${(e: Event) => e.stopPropagation()}>
            <md-icon-button @click=${() => this.handleEditPlan(plan)} aria-label="Edit plan">
              <span class="material-icons">edit</span>
            </md-icon-button>
            <md-icon-button @click=${() => this.handleDeletePlan(plan)} aria-label="Delete plan">
              <span class="material-icons">delete</span>
            </md-icon-button>
          </div>
        </div>

        <div class="meta">
          <span class="material-icons">event</span>
          ${dateRange}
        </div>

        ${plan.location
          ? html`
              <div class="meta">
                <span class="material-icons">place</span>
                ${plan.location.name}
              </div>
            `
          : ''}
        ${plan.description ? html`<div class="description">${plan.description}</div>` : ''}
        ${this.renderCounts(activityCount)}
      </div>
    `
  }

  override render(): unknown {
    return html`
      ${this.syncing
        ? html`<md-linear-progress indeterminate class="sync-bar"></md-linear-progress>`
        : nothing}
      <div
        class="list-content"
        @touchstart=${this.handleTouchStart}
        @touchmove=${this.handleTouchMove}
        @touchend=${this.handleTouchEnd}
      >
        ${this.loaded && this.plans.length === 0
          ? html`
              <div class="empty-state">
                <div class="empty-icon">map</div>
                <h2 class="empty-title">No plans yet</h2>
                <p class="empty-description">
                  Start planning your next expedition! Create a plan to organize routes, gear,
                  timing, and your team.
                </p>
                <md-filled-button @click=${this.handleAddPlan}
                  >Create your first plan</md-filled-button
                >
              </div>
            `
          : html`
              <div class="add-button">
                <md-filled-button @click=${this.handleAddPlan}>
                  <span class="material-icons" slot="icon">add</span>
                  New Plan
                </md-filled-button>
              </div>

              <div class="controls">
                <input
                  class="search-input"
                  type="text"
                  placeholder="Search plans..."
                  .value=${this.searchQuery}
                  @input=${this.handleSearchChange}
                />
              </div>

              ${this.filteredPlans.length === 0
                ? html`
                    <div class="empty-state">
                      <div class="empty-icon">search_off</div>
                      <h2 class="empty-title">No plans found</h2>
                      <p class="empty-description">Try adjusting your search.</p>
                    </div>
                  `
                : html`
                    <div class="plan-grid">
                      ${this.filteredPlans.map(plan => this.renderPlanCard(plan))}
                    </div>
                  `}
            `}
      </div>
      <confirm-dialog></confirm-dialog>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'plan-list': PlanList
  }
}
